import { createBrowserRouter, Outlet, redirect, RouteObject, ScrollRestoration, useNavigate, useParams } from 'react-router'
import api from '../api'

import ErrorPage from './ErrorPage'
import { SidebarLink } from '../ui/Sidebar'
import { LoaderContextProvider, StatefulLoaderContextProvider } from './LoaderContextProvider'
import { AdminContext, CampaignContext, JourneyContext, ListContext, ProjectContext, UserContext } from '../contexts'
import ApiKeys from './settings/ApiKeys'
import EmailEditor from './campaign/editor/EmailEditor'
import Lists from './lists/Lists'
import ListDetail from './lists/ListDetail'
import Users from './users/Users'
import Teams from './settings/Teams'
import Subscriptions from './settings/Subscriptions'
import Transactional from './settings/Transactional'
import UserDetail from './users/UserDetail'
import { createStatefulRoute } from './createStatefulRoute'
import UserDetailAttrs from './users/UserDetailAttrs'
import UserDetailEvents from './users/UserDetailEvents'
import UserDetailSubscriptions from './users/UserDetailSubscriptions'
import CampaignDetail from './campaign/CampaignDetail'
import Campaigns from './campaign/Campaigns'
import CampaignDelivery from './campaign/CampaignDelivery'
import CampaignOverview from './campaign/CampaignOverview'
import CampaignDesign from './campaign/CampaignDesign'
import Journeys from './journey/Journeys'
import JourneyEditor from './journey/JourneyEditor'
import Integrations from './settings/Integrations'
import Tags from './settings/Tags'
import Login from './auth/Login'
import OnboardingStart from './auth/OnboardingStart'
import Onboarding from './auth/Onboarding'
import OnboardingProject from './auth/OnboardingProject'
import { CampaignsIcon, JourneysIcon, ListsIcon, SettingsIcon, UsersIcon } from '../ui/icons'
import { Projects } from './project/Projects'
import { getRecentProjects, pushRecentProject } from '../utils'
import Performance from './organization/Performance'
import Settings, { SettingsIndex } from './settings/Settings'
import { ProjectRoleRequired } from './project/ProjectRoleRequired'
import ProjectSidebar from './project/ProjectSidebar'
import Admins from './organization/Admins'
import Organizations from './organization/Organizations'
import OrganizationSettings from './organization/Settings'
import UserDetailJourneys from './users/UserDetailJourneys'
import { Translation } from 'react-i18next'
import Organization from './organization/Organization'
import DataSchema from './settings/DataSchema'
import GlobalData from './settings/GlobalData'
import Events from './events/Events'
import UserDetailNotifications from './users/UserDetailNotifications'
import CampaignHistory from './campaign/CampaignHistory'
import Resources from './settings/Resources'
import ExportDownload from './exports/ExportDownload'
import Usage from './settings/Usage'
import McpConsent from './mcp-auth/Consent'

export const useRoute = (includeProject = true) => {
    const { projectId = '' } = useParams()
    const navigate = useNavigate()
    const parts: string[] = []
    if (includeProject) {
        parts.push('projects', projectId)
    }
    return (path: string) => {
        parts.push(path)
        navigate('/' + parts.join('/'))?.catch(e => {
            console.error('Failed to navigate to:', e)
        })
    }
}

export interface RouterProps {
    routes?: (routes: RouteObject[]) => RouteObject[]
    projectSidebarLinks?: <T extends SidebarLink>(links: T[]) => T[]
    orgSidebarLinks?: <T extends SidebarLink>(links: T[]) => T[]
}

export const createRouter = ({
    routes = routes => routes,
    projectSidebarLinks = links => links,
    orgSidebarLinks = links => links,
}: RouterProps) => createBrowserRouter(routes([
    {
        path: '/login',
        element: <Login />,
    },
    {
        path: '*',
        errorElement: <ErrorPage />,
        loader: async () => await api.profile.get(),
        element: (
            <LoaderContextProvider context={AdminContext}>
                <ScrollRestoration />
                <Outlet />
            </LoaderContextProvider>
        ),
        children: [
            {
                index: true,
                loader: async () => {
                    const projects = await api.projects.all()
                    if (!projects.length) {
                        return redirect('onboarding')
                    }
                    const recents = getRecentProjects()
                    if (recents.length) {
                        return redirect(`projects/${recents[0].id}`)
                    }
                    return redirect('organization')
                },
                element: <Projects />,
            },
            {
                path: 'mcp-auth/consent',
                element: <McpConsent />,
            },
            {
                path: 'onboarding',
                element: <Onboarding />,
                children: [
                    {
                        index: true,
                        element: <OnboardingStart />,
                    },
                    {
                        path: 'project',
                        element: <OnboardingProject />,
                    },
                ],
            },
            {
                path: 'organization',
                loader: async () => await api.organizations.get(),
                element: <Organization filter={orgSidebarLinks} />,
                children: [
                    {
                        index: true,
                        loader: async () => {
                            return redirect('projects')
                        },
                    },
                    {
                        path: 'projects',
                        element: <Projects />,
                    },
                    {
                        path: 'admins',
                        element: <Admins />,
                    },
                    {
                        path: 'organizations',
                        element: <Organizations />,
                    },
                    {
                        path: 'performance',
                        element: <Performance />,
                    },
                    {
                        path: 'settings',
                        element: <OrganizationSettings />,
                    },
                ],
            },
            {
                path: 'projects/:projectId',
                loader: async ({ params: { projectId = '' } }) => {
                    const project = await api.projects.get(projectId)
                    pushRecentProject(project.id)
                    return project
                },
                element: (
                    <StatefulLoaderContextProvider context={ProjectContext} contextKey="projects">
                        <ProjectSidebar
                            links={projectSidebarLinks([
                                {
                                    key: 'campaigns',
                                    to: 'campaigns',
                                    children: <Translation>{ t => t('campaigns') }</Translation>,
                                    icon: <CampaignsIcon />,
                                    minRole: 'editor',
                                },
                                {
                                    key: 'journeys',
                                    to: 'journeys',
                                    children: <Translation>{ t => t('journeys') }</Translation>,
                                    icon: <JourneysIcon />,
                                    minRole: 'editor',
                                },
                                {
                                    key: 'lists',
                                    to: 'lists',
                                    children: <Translation>{ t => t('lists') }</Translation>,
                                    icon: <ListsIcon />,
                                    minRole: 'editor',
                                },
                                {
                                    key: 'users',
                                    to: 'users',
                                    children: <Translation>{ t => t('users') }</Translation>,
                                    icon: <UsersIcon />,
                                },
                                // {
                                //     key: 'events',
                                //     to: 'events',
                                //     children: <Translation>{ t => t('events') }</Translation>,
                                //     icon: <EventsIcon />,
                                //     minRole: 'admin',
                                // },
                                {
                                    key: 'settings',
                                    to: 'settings',
                                    children: <Translation>{ t => t('settings') }</Translation>,
                                    icon: <SettingsIcon />,
                                    minRole: 'developer',
                                },
                            ])}
                        >
                            <Outlet />
                        </ProjectSidebar>
                    </StatefulLoaderContextProvider>
                ),
                children: [
                    {
                        index: true,
                        loader: async ({ params: { projectId = '' } }) => {
                            const project = await api.projects.get(projectId)
                            if (project.role === 'support') {
                                return redirect(`/projects/${project.id}/users`)
                            }
                            return redirect('campaigns')
                        },
                    },
                    createStatefulRoute({
                        path: 'campaigns',
                        apiPath: api.campaigns,
                        element: <Campaigns />,
                    }),
                    createStatefulRoute({
                        path: 'campaigns/:entityId',
                        apiPath: api.campaigns,
                        context: CampaignContext,
                        element: <CampaignDetail />,
                        children: [
                            {
                                index: true,
                                element: <CampaignOverview />,
                            },
                            {
                                path: 'design',
                                element: <CampaignDesign />,
                            },
                            {
                                path: 'delivery',
                                element: <CampaignDelivery />,
                            },
                            {
                                path: 'history',
                                element: <CampaignHistory />,
                            },
                        ],
                    }),
                    createStatefulRoute({
                        path: 'campaigns/:entityId/editor',
                        apiPath: api.campaigns,
                        context: CampaignContext,
                        element: <EmailEditor />,
                    }),
                    createStatefulRoute({
                        path: 'journeys',
                        apiPath: api.journeys,
                        element: <Journeys />,
                    }),
                    createStatefulRoute({
                        path: 'journeys/:entityId',
                        apiPath: api.journeys,
                        context: JourneyContext,
                        element: <JourneyEditor />,
                        children: [
                            {
                                index: true,
                                element: <JourneyEditor />,
                            },
                        ],
                    }),
                    createStatefulRoute({
                        path: 'users',
                        apiPath: api.users,
                        element: <Users />,
                    }),
                    createStatefulRoute({
                        path: 'users/:entityId',
                        apiPath: api.users,
                        context: UserContext,
                        element: <UserDetail />,
                        children: [
                            {
                                index: true,
                                element: <UserDetailAttrs />,
                            },
                            {
                                path: 'events',
                                element: <UserDetailEvents />,
                            },
                            {
                                path: 'subscriptions',
                                element: <UserDetailSubscriptions />,
                            },
                            {
                                path: 'journeys',
                                element: <UserDetailJourneys />,
                            },
                            {
                                path: 'notifications',
                                element: <UserDetailNotifications />,
                            },
                        ],
                    }),
                    {
                        path: 'events',
                        element: <Events />,
                    },
                    createStatefulRoute({
                        path: 'lists',
                        apiPath: api.lists,
                        element: <Lists />,
                    }),
                    createStatefulRoute({
                        path: 'lists/:entityId',
                        apiPath: api.lists,
                        context: ListContext,
                        element: <ListDetail />,
                    }),
                    {
                        path: 'exports/:exportId/download',
                        element: <ExportDownload />,
                    },
                    {
                        path: 'settings',
                        element: <Settings />,
                        children: [
                            {
                                index: true,
                                element: <SettingsIndex />,
                            },
                            {
                                path: 'team',
                                element: <ProjectRoleRequired minRole="admin"><Teams /></ProjectRoleRequired>,
                            },
                            {
                                path: 'locales',
                                loader: async ({ params: { projectId = '' } }) => redirect(`/projects/${projectId}/settings`),
                            },
                            {
                                path: 'data',
                                element: <DataSchema />,
                            },
                            {
                                path: 'globals',
                                element: <GlobalData />,
                            },
                            {
                                path: 'api-keys',
                                element: <ApiKeys />,
                            },
                            {
                                path: 'integrations',
                                element: <Integrations />,
                            },
                            {
                                path: 'subscriptions',
                                element: <Subscriptions />,
                            },
                            {
                                path: 'webhooks',
                                element: <Transactional />,
                            },
                            {
                                path: 'transactional',
                                loader: async ({ params: { projectId = '' } }) => redirect(`/projects/${projectId}/settings/webhooks`),
                            },
                            {
                                path: 'tags',
                                element: <ProjectRoleRequired minRole="admin"><Tags /></ProjectRoleRequired>,
                            },
                            {
                                path: 'resources',
                                element: <Resources />,
                            },
                            {
                                path: 'usage',
                                element: <ProjectRoleRequired minRole="admin"><Usage /></ProjectRoleRequired>,
                            },
                            {
                                path: 'performance',
                                element: <ProjectRoleRequired minRole="admin"><Performance /></ProjectRoleRequired>,
                            },
                        ],
                    },
                ],
            },
            {
                path: '*',
                element: <ErrorPage status={404} />,
                errorElement: <ErrorPage />,
            },
        ],
    },
]))
